import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, resolveDirective as _resolveDirective, openBlock as _openBlock, createBlock as _createBlock, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, withKeys as _withKeys, withModifiers as _withModifiers, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createSlots as _createSlots } from "vue"

const _hoisted_1 = { class: "system-monitor" }
const _hoisted_2 = { class: "card-header" }
const _hoisted_3 = { class: "card-title" }
const _hoisted_4 = { class: "stat-item stat-item--primary" }
const _hoisted_5 = { class: "stat-value" }
const _hoisted_6 = { class: "stat-item stat-item--danger" }
const _hoisted_7 = { class: "stat-value" }
const _hoisted_8 = { class: "stat-item stat-item--success" }
const _hoisted_9 = { class: "stat-value" }
const _hoisted_10 = { class: "section-title" }
const _hoisted_11 = { class: "section-title" }
const _hoisted_12 = ["onClick"]
const _hoisted_13 = { key: 2 }
const _hoisted_14 = { class: "card-header" }
const _hoisted_15 = { class: "card-title" }
const _hoisted_16 = { class: "status-value" }
const _hoisted_17 = { class: "json-viewer-body" }
const _hoisted_18 = { class: "json-viewer-pre" }

import { onMounted, reactive, ref } from 'vue'
import { ElMessage } from 'element-plus'
import {
  CopyDocument,
  Document,
  Monitor,
  Refresh,
  Search,
  User,
  UserFilled,
} from '@element-plus/icons-vue'
import SystemMonitorApi, { TodayAccessLogItem } from '@/network/apis/SystemMonitor'

interface ActiveUser {
  user?: string
  count?: number
}

interface LogColumn {
  prop?: string
  label: string
  width?: number
  minWidth?: number
  align?: 'left' | 'center' | 'right'
  fixed?: boolean | 'left' | 'right'
  type?: 'text' | 'json' | 'reqres'
  showOverflowTooltip?: boolean
}

// 字段名 → 中文表头（仅用于翻译标签，不是列白名单：
// 后端数据里有这个字段就显示，没有就不显示，未知字段直接用字段名）

export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const statusFields = [
  { label: '环境名称', key: 'EnvironmentName' },
  { label: '系统架构', key: 'OSArchitecture' },
  { label: '内容根路径', key: 'ContentRootPath' },
  { label: 'Web根路径', key: 'WebRootPath' },
  { label: '框架描述', key: 'FrameworkDescription' },
  { label: '内存占用', key: 'MemoryFootprint' },
  { label: '运行时长', key: 'WorkingTime' },
]

const loading = ref(false)
const statusLoading = ref(false)
const logLoading = ref(false)

const activeUsers = ref<ActiveUser[]>([])
const activeUserCount = ref(0)
const errorCount = ref(0)
const status = ref<Record<string, any>>({})

// 今日访问日志
const todayLogs = ref<TodayAccessLogItem[]>([])

// 搜索表单（输入框不填就不传）
const logSearchForm = reactive<{
  User: string
  url: string
  start: string
  end: string
}>({
  User: '',
  url: '',
  start: '',
  end: '',
})

// ========== 日志表格动态列 ==========
const FIELD_LABELS: Record<string, string> = {
  Time: '请求时间',
  User: '用户',
  IP: 'IP',
  Path: '接口',
  QueryString: 'Query参数',
  BodyData: '请求体',
  ResponseData: '响应数据',
  TraceId: 'TraceId',
  HasRequest: '请求/响应',
  HasResponse: '请求/响应',
}

// 根据后端返回的数据字段自动生成表头（后端返回什么字段，表头就显示什么）
const buildColumnsFromData = (items: any[]): LogColumn[] => {
  if (!Array.isArray(items) || items.length === 0) return []
  const keys = new Set<string>()
  items.forEach((item) => {
    if (item && typeof item === 'object') {
      Object.keys(item).forEach((k) => keys.add(k))
    }
  })
  const cols: LogColumn[] = []
  keys.forEach((key) => {
    const label = FIELD_LABELS[key] ?? key
    if (key === 'BodyData' || key === 'ResponseData') {
      // JSON 内容列：格式化 + 点击弹窗
      cols.push({
        prop: key,
        label,
        minWidth: key === 'ResponseData' ? 300 : 260,
        type: 'json',
      })
    } else if (key === 'HasRequest' || key === 'HasResponse') {
      // 请求/响应标记合并为一列（HasResponse 不重复生成）
      if (key === 'HasRequest') {
        cols.push({ prop: key, label, minWidth: 100, align: 'center', type: 'reqres' })
      }
    } else {
      cols.push({
        prop: key,
        label,
        minWidth: key === 'Time' ? 160 : 130,
        align: key === 'Time' ? 'center' : 'left',
        fixed: key === 'Time' ? 'left' : undefined,
        showOverflowTooltip: true,
      })
    }
  })
  return cols
}

const logColumns = ref<LogColumn[]>([])

// 从接口响应解析后端返回的列定义（后端显式返回 columns 时优先使用）
const parseColumns = (res: any): LogColumn[] | null => {
  const rawCols = res?.columns ?? res?.response?.columns ?? null
  if (!Array.isArray(rawCols) || rawCols.length === 0) return null
  const cols = rawCols
    .map((c: any) => ({
      prop: c.prop ?? c.field ?? c.key,
      label: c.label ?? c.title ?? c.name ?? '',
      width: c.width,
      minWidth: c.minWidth ?? c.min_width,
      align: c.align,
      fixed: c.fixed,
      type: c.type,
      showOverflowTooltip: c.showOverflowTooltip,
    }))
    .filter((c: LogColumn) => c.prop && c.label)
  return cols.length > 0 ? cols : null
}

// 时间范围选择器：[开始时间, 结束时间]
const logTimeRange = ref<[string, string] | null>(null)

// datetimerange 的 default-time 必须是 Date 对象数组，不能传字符串数组
const defaultTimeRange: [Date, Date] = [
  new Date(0, 0, 0, 0, 0, 0),
  new Date(0, 0, 0, 23, 59, 59),
]

// 构造请求参数：去掉空字符串字段
const buildLogParams = () => {
  // 将时间范围选择器的值同步到表单字段
  if (logTimeRange.value && logTimeRange.value.length === 2) {
    logSearchForm.start = logTimeRange.value[0] || ''
    logSearchForm.end = logTimeRange.value[1] || ''
  } else {
    logSearchForm.start = ''
    logSearchForm.end = ''
  }
  const params: Record<string, string> = {}
  for (const [k, v] of Object.entries(logSearchForm)) {
    const value = (v ?? '').trim()
    if (value) params[k] = value
  }
  return params
}

const loadLogs = async (params: Record<string, string> = {}) => {
  logLoading.value = true
  try {
    const res: any = await SystemMonitorApi.getTodayAccessLogDetails(params)
    const data = res?.response
    todayLogs.value = Array.isArray(data) ? data : []
    // 表头生成优先级：后端显式返回 columns > 从返回数据字段自动生成（有数据时）
    const cols = parseColumns(res)
    if (cols) {
      logColumns.value = cols
    } else if (todayLogs.value.length > 0) {
      logColumns.value = buildColumnsFromData(todayLogs.value)
    } else {
      logColumns.value = []
    }
  } catch (err) {
    console.error('获取今日访问日志失败:', err)
    todayLogs.value = []
  } finally {
    logLoading.value = false
  }
}

const onSearchLogs = () => {
  loadLogs(buildLogParams())
}

const onResetLogs = () => {
  logSearchForm.User = ''
  logSearchForm.url = ''
  logSearchForm.start = ''
  logSearchForm.end = ''
  logTimeRange.value = null
  todayLogs.value = []
  logColumns.value = []
}

// 刷新：保留当前筛选条件重新加载最新日志
const onRefreshLogs = () => {
  loadLogs(buildLogParams())
}

// ========== JSON 内容格式化展示 ==========
const jsonViewerVisible = ref(false)
const jsonViewerTitle = ref('')
const jsonViewerContent = ref('')

// 尝试将字符串按 JSON 格式化，解析失败则原样返回（如 "[文件响应]..."、"Status Code: 404" 等）
const formatJsonText = (value: any): string => {
  if (value == null || value === '') return ''
  const raw = String(value)
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    return raw
  }
}

const openJsonViewer = (value: any, title: string) => {
  jsonViewerContent.value = formatJsonText(value)
  jsonViewerTitle.value = title
  jsonViewerVisible.value = true
}

const copyJsonViewer = async () => {
  try {
    await navigator.clipboard.writeText(jsonViewerContent.value)
    ElMessage.success('已复制到剪贴板')
  } catch {
    ElMessage.error('复制失败，请手动选择复制')
  }
}

const loadActiveUsers = async () => {
  loading.value = true
  try {
    const res: any = await SystemMonitorApi.getActiveUsers()
    const data = res?.response || {}
    activeUsers.value = Array.isArray(data.activeUsers) ? data.activeUsers : []
    activeUserCount.value = data.activeUserCount ?? activeUsers.value.length
    errorCount.value = data.errorCount ?? 0
  } catch (err) {
    console.error('获取活跃用户失败:', err)
  } finally {
    loading.value = false
  }
}

const loadStatus = async () => {
  statusLoading.value = true
  try {
    const res: any = await SystemMonitorApi.getSystemStatus()
    status.value = res?.response || {}
  } catch (err) {
    console.error('获取系统运行状态失败:', err)
  } finally {
    statusLoading.value = false
  }
}

const loadAll = async () => {
  await Promise.all([loadActiveUsers(), loadStatus()])
}

onMounted(loadAll)

return (_ctx: any,_cache: any) => {
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_row = _resolveComponent("el-row")!
  const _component_el_table_column = _resolveComponent("el-table-column")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_table = _resolveComponent("el-table")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_date_picker = _resolveComponent("el-date-picker")!
  const _component_el_form = _resolveComponent("el-form")!
  const _component_el_card = _resolveComponent("el-card")!
  const _component_el_descriptions_item = _resolveComponent("el-descriptions-item")!
  const _component_el_descriptions = _resolveComponent("el-descriptions")!
  const _component_el_dialog = _resolveComponent("el-dialog")!
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_el_row, {
      gutter: 16,
      class: "monitor-row"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_el_col, {
          xs: 24,
          sm: 24,
          md: 16
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_card, {
              shadow: "hover",
              class: "monitor-card"
            }, {
              header: _withCtx(() => [
                _createElementVNode("div", _hoisted_2, [
                  _createElementVNode("span", _hoisted_3, [
                    _createVNode(_component_el_icon, { class: "title-icon" }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(User))
                      ]),
                      _: 1
                    }),
                    _cache[6] || (_cache[6] = _createTextVNode(" 活跃用户 "))
                  ]),
                  _createVNode(_component_el_button, {
                    type: "primary",
                    plain: "",
                    size: "small",
                    loading: loading.value,
                    onClick: loadActiveUsers
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_icon, null, {
                        default: _withCtx(() => [
                          _createVNode(_unref(Refresh))
                        ]),
                        _: 1
                      }),
                      _cache[7] || (_cache[7] = _createElementVNode("span", { style: {"margin-left":"4px"} }, "刷新", -1))
                    ]),
                    _: 1
                  }, 8, ["loading"])
                ])
              ]),
              default: _withCtx(() => [
                _createVNode(_component_el_row, {
                  gutter: 12,
                  class: "stat-row"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_col, { span: 8 }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_4, [
                          _createElementVNode("div", _hoisted_5, _toDisplayString(activeUserCount.value), 1),
                          _cache[8] || (_cache[8] = _createElementVNode("div", { class: "stat-label" }, "当前活跃用户", -1))
                        ])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_col, { span: 8 }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_6, [
                          _createElementVNode("div", _hoisted_7, _toDisplayString(errorCount.value), 1),
                          _cache[9] || (_cache[9] = _createElementVNode("div", { class: "stat-label" }, "错误次数", -1))
                        ])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_col, { span: 8 }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_8, [
                          _createElementVNode("div", _hoisted_9, _toDisplayString(activeUsers.value.length), 1),
                          _cache[10] || (_cache[10] = _createElementVNode("div", { class: "stat-label" }, "用户条目", -1))
                        ])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createElementVNode("div", _hoisted_10, [
                  _createVNode(_component_el_icon, null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(UserFilled))
                    ]),
                    _: 1
                  }),
                  _cache[11] || (_cache[11] = _createElementVNode("span", { style: {"margin-left":"4px"} }, "用户列表", -1))
                ]),
                _withDirectives((_openBlock(), _createBlock(_component_el_table, {
                  data: activeUsers.value,
                  border: "",
                  stripe: "",
                  size: "default",
                  class: "user-table"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_table_column, {
                      type: "index",
                      label: "序号",
                      width: "70",
                      align: "center"
                    }),
                    _createVNode(_component_el_table_column, {
                      prop: "user",
                      label: "用户名",
                      "min-width": "120",
                      "show-overflow-tooltip": ""
                    }),
                    _createVNode(_component_el_table_column, {
                      prop: "count",
                      label: "访问次数",
                      "min-width": "100",
                      align: "center"
                    }, {
                      default: _withCtx(({ row }) => [
                        _createVNode(_component_el_tag, {
                          type: "primary",
                          size: "small"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(row.count ?? 0), 1)
                          ]),
                          _: 2
                        }, 1024)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["data"])), [
                  [_directive_loading, loading.value]
                ]),
                _createElementVNode("div", _hoisted_11, [
                  _createVNode(_component_el_icon, null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(Document))
                    ]),
                    _: 1
                  }),
                  _cache[13] || (_cache[13] = _createElementVNode("span", { style: {"margin-left":"4px"} }, "今日访问日志", -1)),
                  (todayLogs.value.length || logLoading.value)
                    ? (_openBlock(), _createBlock(_component_el_tag, {
                        key: 0,
                        size: "small",
                        type: "info",
                        effect: "plain",
                        style: {"margin-left":"8px"}
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(" 共 " + _toDisplayString(todayLogs.value.length) + " 条 ", 1)
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  _createVNode(_component_el_button, {
                    type: "primary",
                    plain: "",
                    size: "small",
                    class: "section-refresh-btn",
                    loading: logLoading.value,
                    onClick: onRefreshLogs
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_icon, null, {
                        default: _withCtx(() => [
                          _createVNode(_unref(Refresh))
                        ]),
                        _: 1
                      }),
                      _cache[12] || (_cache[12] = _createElementVNode("span", { style: {"margin-left":"4px"} }, "刷新", -1))
                    ]),
                    _: 1
                  }, 8, ["loading"])
                ]),
                _createVNode(_component_el_form, {
                  inline: true,
                  model: logSearchForm,
                  class: "log-search-form",
                  onSubmit: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["prevent"]))
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_form_item, { label: "用户" }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_input, {
                          modelValue: logSearchForm.User,
                          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((logSearchForm.User) = $event)),
                          placeholder: "用户名",
                          clearable: "",
                          size: "default",
                          style: {"width":"140px"},
                          onKeyup: _withKeys(onSearchLogs, ["enter"])
                        }, null, 8, ["modelValue"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_form_item, { label: "接口URL" }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_input, {
                          modelValue: logSearchForm.url,
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((logSearchForm.url) = $event)),
                          placeholder: "接口路径/关键字",
                          clearable: "",
                          size: "default",
                          style: {"width":"200px"},
                          onKeyup: _withKeys(onSearchLogs, ["enter"])
                        }, null, 8, ["modelValue"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_form_item, { label: "时间范围" }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_date_picker, {
                          modelValue: logTimeRange.value,
                          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((logTimeRange).value = $event)),
                          type: "datetimerange",
                          "range-separator": "~",
                          "start-placeholder": "开始时间",
                          "end-placeholder": "结束时间",
                          format: "YYYY-MM-DD HH:mm:ss",
                          "value-format": "YYYY-MM-DD HH:mm:ss",
                          "default-time": defaultTimeRange,
                          style: {"width":"380px"}
                        }, null, 8, ["modelValue"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_form_item, null, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_button, {
                          type: "primary",
                          loading: logLoading.value,
                          onClick: onSearchLogs
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_icon, null, {
                              default: _withCtx(() => [
                                _createVNode(_unref(Search))
                              ]),
                              _: 1
                            }),
                            _cache[14] || (_cache[14] = _createElementVNode("span", { style: {"margin-left":"4px"} }, "搜索", -1))
                          ]),
                          _: 1
                        }, 8, ["loading"]),
                        _createVNode(_component_el_button, {
                          disabled: logLoading.value,
                          onClick: onResetLogs
                        }, {
                          default: _withCtx(() => _cache[15] || (_cache[15] = [
                            _createTextVNode("重置")
                          ])),
                          _: 1
                        }, 8, ["disabled"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["model"]),
                _withDirectives((_openBlock(), _createBlock(_component_el_table, {
                  data: todayLogs.value,
                  border: "",
                  stripe: "",
                  height: "480",
                  class: "log-table"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_table_column, {
                      type: "index",
                      label: "#",
                      width: "55",
                      align: "center",
                      fixed: "left"
                    }),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(logColumns.value, (col) => {
                      return (_openBlock(), _createBlock(_component_el_table_column, {
                        key: `${col.prop || col.label}-${logColumns.value.length}`,
                        prop: col.prop,
                        label: col.label,
                        width: col.width,
                        "min-width": col.minWidth,
                        align: col.align || 'left',
                        fixed: col.fixed,
                        "show-overflow-tooltip": col.type !== 'json' && col.type !== 'reqres' && col.showOverflowTooltip !== false
                      }, _createSlots({ _: 2 }, [
                        (col.type === 'json')
                          ? {
                              name: "default",
                              fn: _withCtx(({ row }) => [
                                _createElementVNode("div", {
                                  class: "json-cell",
                                  onClick: ($event: any) => (openJsonViewer(row[col.prop!], col.label))
                                }, _toDisplayString(formatJsonText(row[col.prop!])), 9, _hoisted_12)
                              ]),
                              key: "0"
                            }
                          : (col.type === 'reqres')
                            ? {
                                name: "default",
                                fn: _withCtx(({ row }) => [
                                  (row.HasRequest)
                                    ? (_openBlock(), _createBlock(_component_el_tag, {
                                        key: 0,
                                        size: "small",
                                        type: "success",
                                        effect: "plain"
                                      }, {
                                        default: _withCtx(() => _cache[16] || (_cache[16] = [
                                          _createTextVNode("Req")
                                        ])),
                                        _: 1
                                      }))
                                    : _createCommentVNode("", true),
                                  (row.HasResponse)
                                    ? (_openBlock(), _createBlock(_component_el_tag, {
                                        key: 1,
                                        size: "small",
                                        type: "primary",
                                        effect: "plain",
                                        style: {"margin-left":"4px"}
                                      }, {
                                        default: _withCtx(() => _cache[17] || (_cache[17] = [
                                          _createTextVNode("Res")
                                        ])),
                                        _: 1
                                      }))
                                    : _createCommentVNode("", true),
                                  (!row.HasRequest && !row.HasResponse)
                                    ? (_openBlock(), _createElementBlock("span", _hoisted_13, "-"))
                                    : _createCommentVNode("", true)
                                ]),
                                key: "1"
                              }
                            : undefined
                      ]), 1032, ["prop", "label", "width", "min-width", "align", "fixed", "show-overflow-tooltip"]))
                    }), 128))
                  ]),
                  _: 1
                }, 8, ["data"])), [
                  [_directive_loading, logLoading.value]
                ])
              ]),
              _: 1
            })
          ]),
          _: 1
        }),
        _createVNode(_component_el_col, {
          xs: 24,
          sm: 24,
          md: 8
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_card, {
              shadow: "hover",
              class: "monitor-card status-card"
            }, {
              header: _withCtx(() => [
                _createElementVNode("div", _hoisted_14, [
                  _createElementVNode("span", _hoisted_15, [
                    _createVNode(_component_el_icon, { class: "title-icon" }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(Monitor))
                      ]),
                      _: 1
                    }),
                    _cache[18] || (_cache[18] = _createTextVNode(" 系统运行状态 "))
                  ]),
                  _createVNode(_component_el_button, {
                    type: "primary",
                    plain: "",
                    size: "small",
                    loading: statusLoading.value,
                    onClick: loadStatus
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_icon, null, {
                        default: _withCtx(() => [
                          _createVNode(_unref(Refresh))
                        ]),
                        _: 1
                      }),
                      _cache[19] || (_cache[19] = _createElementVNode("span", { style: {"margin-left":"4px"} }, "刷新", -1))
                    ]),
                    _: 1
                  }, 8, ["loading"])
                ])
              ]),
              default: _withCtx(() => [
                _createVNode(_component_el_descriptions, {
                  column: 1,
                  border: "",
                  class: "status-descriptions"
                }, {
                  default: _withCtx(() => [
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(statusFields, (field) => {
                      return _createVNode(_component_el_descriptions_item, {
                        key: field.key,
                        label: field.label,
                        "label-align": "right",
                        "label-width": "110px",
                        align: "left"
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("span", _hoisted_16, _toDisplayString(status.value[field.key] ?? '-'), 1)
                        ]),
                        _: 2
                      }, 1032, ["label"])
                    }), 64))
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createVNode(_component_el_dialog, {
      modelValue: jsonViewerVisible.value,
      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((jsonViewerVisible).value = $event)),
      title: jsonViewerTitle.value,
      width: "70%",
      top: "5vh",
      "append-to-body": ""
    }, {
      footer: _withCtx(() => [
        _createVNode(_component_el_button, {
          type: "primary",
          onClick: copyJsonViewer
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_icon, null, {
              default: _withCtx(() => [
                _createVNode(_unref(CopyDocument))
              ]),
              _: 1
            }),
            _cache[20] || (_cache[20] = _createElementVNode("span", { style: {"margin-left":"4px"} }, "复制内容", -1))
          ]),
          _: 1
        }),
        _createVNode(_component_el_button, {
          onClick: _cache[4] || (_cache[4] = ($event: any) => (jsonViewerVisible.value = false))
        }, {
          default: _withCtx(() => _cache[21] || (_cache[21] = [
            _createTextVNode("关闭")
          ])),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_17, [
          _createElementVNode("pre", _hoisted_18, _toDisplayString(jsonViewerContent.value), 1)
        ])
      ]),
      _: 1
    }, 8, ["modelValue", "title"])
  ]))
}
}

})